import {
    Button,
    createStyles,
    IconButton,
    Theme,
    withStyles,
    WithStyles,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from "@material-ui/core";
import {
    MoreHoriz,
    SkipNext,
    SkipPrevious,
    ArrowLeft,
    ArrowRight,
    Delete
} from "@material-ui/icons";
import React from "react";
import ReactDOM from "react-dom";
import ImageGallery, { ReactImageGalleryItem } from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import Constants from "../../models/utility/Constants";

type ImageCarouselProps = WithStyles<typeof styles> & {
    backButtonText?: string;
    deleteFn?: (name: string) => (e: React.MouseEvent) => void;
    reorderFn?: (
        name: string,
        newIndex: number
    ) => (e: React.MouseEvent) => void;
    fixedRatio?: boolean;
    images: ReactImageGalleryItem[];
    maxWidth?: number;
    showThumbnailControls?: boolean;
    showThumbnails?: boolean;
};

interface IImageCarouselState {
    fullScreen: boolean;
    maxHeight: number;
    thumbMenuEl: HTMLElement | null;
    thumbMenuImageId: string | null;
}

const maxWidth = 600;
const ratio = 1.5;
const styles = (theme: Theme) =>
    createStyles({
        backButtonDiv: {
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            left: 0,
            padding: theme.spacing(4),
            position: "fixed",
            right: 0,
            width: "100%",
            zIndex: 1500
        },
        carousel: {
            "& .image-gallery-fullscreen-button:hover:before": {
                color: theme.palette.primary.main
            },
            "& .image-gallery-left-nav:hover:before": {
                color: theme.palette.primary.main
            },
            "& .image-gallery-play-button:hover:before": {
                color: theme.palette.primary.main
            },
            "& .image-gallery-right-nav:hover:before": {
                color: theme.palette.primary.main
            },
            "& .image-gallery-slide": {
                backgroundColor: "transparent"
            },
            "& .image-gallery-slide img": {
                maxHeight: maxWidth / ratio,
                width: Constants.IsIE ? "100% !important" : "auto !important"
            },
            "& .image-gallery-slide-wrapper": {
                backgroundColor: theme.palette.background.overlay
            },
            "& .image-gallery-thumbnail.active": {
                border: `4px solid ${theme.palette.primary.main}`
            },
            "& .image-gallery.fullscreen-modal": {
                background: theme.palette.background.overlay,
                zIndex: theme.zIndex.tooltip
            },
            flexGrow: 1,
            maxWidth
        },
        thumbDelete: {
            padding: theme.spacing(0.5),
            position: "absolute",
            right: 0,
            top: 0
        },
        thumbDeleteBtn: {
            color: theme.palette.common.white,
            padding: 0
        },
        thumbMoreIcon: {
            filter: "drop-shadow(2px 2px 2px black)",
            msFilter: "drop-shadow(2px 2px 2px black)",
            webkitFilter: "drop-shadow(2px 2px 2px black)"
        },
        deleteMenuItem: {
            color: theme.palette.error.main
        },
        arrowMenuItemIcon: {
            marginRight: 0
        },
        arrowMenuItemIconSvg: {
            marginLeft: -8,
            marginRight: 12
        }
    });

class ImageCarousel extends React.PureComponent<
    ImageCarouselProps,
    IImageCarouselState
> {
    private ref: React.RefObject<ImageGallery>;

    constructor(props: ImageCarouselProps) {
        super(props);

        this.ref = React.createRef();
        this.state = {
            fullScreen: false,
            maxHeight: maxWidth / ratio,
            thumbMenuEl: null,
            thumbMenuImageId: null
        };
    }

    public componentDidMount() {
        if (this.props.fixedRatio) {
            window.addEventListener(
                "deviceorientation",
                this.resizeImageDiv.bind(this)
            );
            window.addEventListener(
                "orientationchange",
                this.resizeImageDiv.bind(this)
            );
            window.addEventListener("resize", this.resizeImageDiv.bind(this));

            this.resizeImageDiv();
        }
    }

    public componentWillUnmount() {
        if (this.props.fixedRatio) {
            window.removeEventListener(
                "deviceorientation",
                this.resizeImageDiv
            );
            window.removeEventListener(
                "orientationchange",
                this.resizeImageDiv
            );
            window.removeEventListener("resize", this.resizeImageDiv);
        }
    }

    public render() {
        const classes = this.props.classes;
        const propStyle = {
            maxWidth: this.props.maxWidth || maxWidth
        } as React.CSSProperties;

        const showThumbnails =
            this.props.showThumbnails !== undefined ||
            this.props.showThumbnails !== null
                ? this.props.showThumbnails
                : true;

        let images = this.props.images;
        images = [
            ...this.props.images.map((e, i, a) => {
                return {
                    ...e,
                    renderItem: this.renderImage,
                    renderThumbInner: this.props.showThumbnailControls
                        ? this.renderThumbWithMenu(i, a)
                        : undefined
                };
            })
        ];

        return (
            <div
                className={`${classes.carousel} ${
                    showThumbnails || this.state.fullScreen
                        ? ""
                        : "hideThumbnails"
                }`}
                style={propStyle}
            >
                <ImageGallery
                    ref={this.ref}
                    items={images}
                    showPlayButton={false}
                    useBrowserFullscreen={false}
                    showBullets={true}
                    showThumbnails={true}
                    onScreenChange={this.onFullScreen}
                    lazyLoad={true}
                />

                {this.state.fullScreen && this.renderBackButton()}
            </div>
        );
    }

    private renderBackButton = () => {
        const classes = this.props.classes;

        return (
            <div className={classes.backButtonDiv}>
                <Button
                    size="large"
                    variant="outlined"
                    color="primary"
                    onClick={this.ref.current?.exitFullScreen.bind(
                        this.ref.current
                    )}
                >
                    {this.props.backButtonText || "Go Back"}
                </Button>
            </div>
        );
    };

    private resizeImageDiv = () => {
        const root = this.ref.current;
        if (root) {
            // find affected div
            const node = ReactDOM.findDOMNode(root) as HTMLDivElement;
            const galleryDiv = node.getElementsByClassName(
                "image-gallery-slide-wrapper"
            )[0];

            // maintain aspect ratio
            const width = galleryDiv.clientWidth;

            const maxHeight = (this.props.maxWidth || maxWidth) / ratio;
            const newHeight = Math.min(maxHeight, width / ratio);

            galleryDiv.setAttribute("style", `height: ${newHeight}px;`);

            if (this.props.fixedRatio) {
                this.setState({ maxHeight: newHeight });
            }
        }
    };

    private renderImage = (item: ReactImageGalleryItem) => {
        let style = {};
        if (this.props.fixedRatio) {
            style = { maxHeight: this.state.maxHeight };
        }

        return (
            <img
                src={item.original}
                alt={item.originalAlt}
                title={item.originalTitle}
                style={style}
            />
        );
    };

    handleThumbClick =
        (imageId: string) =>
        (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
            this.setState({
                thumbMenuEl: event.currentTarget,
                thumbMenuImageId: imageId
            });
        };

    handleThumbClose = () => {
        this.setState({ thumbMenuEl: null, thumbMenuImageId: null });
    };

    handleThumbDelete =
        (item: ReactImageGalleryItem) =>
        (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
            if (this.props.deleteFn) {
                this.props.deleteFn(item.originalAlt || "")(event);
            }
            this.handleThumbClose();
        };

    handleThumbReorder =
        (item: ReactImageGalleryItem, newIndex: number) =>
        (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
            if (this.props.reorderFn) {
                this.props.reorderFn(item.originalAlt || "", newIndex)(event);
            }
            this.handleThumbClose();
        };

    private renderThumbWithMenu =
        (index: number, array: ReactImageGalleryItem[]) =>
        (item: ReactImageGalleryItem) => {
            const classes = this.props.classes;

            return (
                <div className="image-gallery-thumbnail-inner">
                    {!!this.props.deleteFn && item.originalAlt && (
                        <div className={classes.thumbDelete}>
                            <IconButton
                                className={classes.thumbDeleteBtn}
                                onClick={this.handleThumbClick(
                                    item.originalAlt
                                )}
                            >
                                <MoreHoriz
                                    className={classes.thumbMoreIcon}
                                    color="inherit"
                                />
                            </IconButton>
                            <Menu
                                id={"image-gallery-thumb-menu-" + index}
                                anchorEl={this.state.thumbMenuEl}
                                open={
                                    this.state.thumbMenuImageId ===
                                    item.originalAlt
                                }
                                onClose={this.handleThumbClose}
                            >
                                {index > 0 && (
                                    <MenuItem
                                        onClick={this.handleThumbReorder(
                                            item,
                                            index - 1
                                        )}
                                    >
                                        <ListItemIcon
                                            className={
                                                classes.arrowMenuItemIcon
                                            }
                                        >
                                            <ArrowLeft
                                                className={
                                                    classes.arrowMenuItemIconSvg
                                                }
                                                fontSize="large"
                                            />
                                        </ListItemIcon>
                                        <ListItemText>Move Left</ListItemText>
                                    </MenuItem>
                                )}
                                {index > 0 && (
                                    <MenuItem
                                        onClick={this.handleThumbReorder(
                                            item,
                                            0
                                        )}
                                    >
                                        <ListItemIcon>
                                            <SkipPrevious />
                                        </ListItemIcon>
                                        <ListItemText>
                                            Move to First
                                        </ListItemText>
                                    </MenuItem>
                                )}
                                {index < array.length - 1 && (
                                    <MenuItem
                                        onClick={this.handleThumbReorder(
                                            item,
                                            index + 1
                                        )}
                                    >
                                        <ListItemIcon
                                            className={
                                                classes.arrowMenuItemIcon
                                            }
                                        >
                                            <ArrowRight
                                                className={
                                                    classes.arrowMenuItemIconSvg
                                                }
                                                fontSize="large"
                                            />
                                        </ListItemIcon>
                                        <ListItemText>Move Right</ListItemText>
                                    </MenuItem>
                                )}
                                {index < array.length - 1 && (
                                    <MenuItem
                                        onClick={this.handleThumbReorder(
                                            item,
                                            array.length - 1
                                        )}
                                    >
                                        <ListItemIcon>
                                            <SkipNext />
                                        </ListItemIcon>
                                        <ListItemText>
                                            Move to Last
                                        </ListItemText>
                                    </MenuItem>
                                )}
                                <MenuItem
                                    onClick={this.handleThumbDelete(item)}
                                >
                                    <ListItemIcon
                                        className={classes.deleteMenuItem}
                                    >
                                        <Delete />
                                    </ListItemIcon>
                                    <ListItemText
                                        primaryTypographyProps={{
                                            className: classes.deleteMenuItem
                                        }}
                                    >
                                        Delete Image
                                    </ListItemText>
                                </MenuItem>
                            </Menu>
                        </div>
                    )}
                    <img
                        src={item.thumbnail}
                        alt={item.thumbnailAlt}
                        title={item.thumbnailTitle}
                    />
                    {item.thumbnailLabel && (
                        <div className="image-gallery-thumbnail-label">
                            {item.thumbnailLabel}
                        </div>
                    )}
                </div>
            );
        };

    private onFullScreen = (e: Element) => this.setState({ fullScreen: !!e });
}

export default withStyles(styles)(ImageCarousel);
